import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Aqua signature "lozenge": a vertical gradient (lighter stop on top)
        // plus a blue glow, verbatim from design-reference/aqua/ `.btn.prim`.
        // Tokens are themed in design-tokens.css, so dark mode gets its own
        // gradient top + glow automatically.
        // NOTE: `[box-shadow:var(--…)]` (arbitrary PROPERTY), not `shadow-[var(--…)]`
        // — the latter feeds the var through Tailwind's --tw-shadow pipeline,
        // which cannot parse a multi-part shadow and silently drops the glow.
        default:
          "bg-[linear-gradient(180deg,var(--primary-grad-top),var(--primary))] text-primary-foreground [box-shadow:var(--shadow-aqua-btn)] tracking-[-0.01em] hover:brightness-110",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive-hover",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        neon: "bg-primary text-primary-foreground hover:shadow-glow-primary border border-primary-400/30",
        glass: "glass text-foreground hover:bg-accent/10 border-0",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3 text-xs",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const isDisabled = disabled || loading
    // A `title` on a DISABLED button is unreachable: `disabled:pointer-events-none`
    // above stops hover, and browsers suppress tooltips on a disabled control
    // anyway. Call sites walk into this by supplying the reason exactly when the
    // button is off — e.g. IngestSourceSection's "Only an owner or admin can
    // sync" — so the explanation was never shown to anyone.
    //
    // When a disabled button carries a reason, repeat it on a wrapper, which is
    // not disabled and therefore does get hover. The attribute stays on the
    // button too: callers and tests read it there, and the inner one simply
    // never surfaces.
    //
    // Deliberately NOT adding an sr-only copy of the text. The first attempt did,
    // and it broke pages that already render the reason visibly (PlaybookEditor,
    // SearchConnectionPicker) by matching the same string twice — a duplicate
    // announcement for screen readers as much as a duplicate match for tests.
    //
    // Narrow on purpose: only when disabled AND a non-empty string title AND not
    // asChild — wrapping a Slot child would break its single-child contract, and
    // wrapping every button would change layout app-wide. Visible text beside the
    // control remains the better pattern where there is room (see
    // OutboundPlaybooksPage); this makes the reasons that already exist reachable.
    const reason = typeof props.title === "string" ? props.title.trim() : ""
    if (isDisabled && reason && !asChild) {
      return (
        <span className="inline-flex" title={reason}>
          <Comp
            className={cn(buttonVariants({ variant, size, className }))}
            ref={ref}
            disabled
            {...props}
          >
            {loading && <span className="spinner mr-1" />}
            {children}
          </Comp>
        </span>
      )
    }
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {loading && <span className="spinner mr-1" />}
        {children}
      </Comp>
    )
  }
)
Button.displayName = "Button"

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants }
